import { sanitize as storybookSanitize } from '@storybook/csf'
import { allCountries } from 'country-telephone-data';
import moment from 'moment';

const DEFAULT_PHONE_ISO_KEY = 'de'

export const breakpoints = {
    xs: 375,
    sm: 768,
    md: 1024,
    lg: 1280,
    xl: 1600,
    container: 1920 + 64,
}

export const hexBackgroundColors = {
    yellow: '#fec100',
    purple: '#ba8af7',
    red: '#ff1d1d',
    green: '#5bea07',
    blue: '#0695ec',
    black: '#000000',
    white: '#ffffff',
    pink: '#ff5fce',
    salmon: '#ffb3b3',
    mint: '#00f982',
    indigo: '#006dff',
    orange: '#ff8022',
    grey: '#dcdce6',
    'dark-grey': '#dcdcdc',
    'light-grey': '#f0f0f0',
}

export const getDefaultPhoneCode = (isoKey = DEFAULT_PHONE_ISO_KEY) =>
'+' + allCountries.find((country) => country.iso2 === isoKey)?.dialCode || ''

export const getPlaceholderImageSrc = (width: number = 100, height: number = 100) => `https://placehold.co/${width}x${height}.png`

export const isBreakpoint = (breakpoint: number): boolean => {
    return window.matchMedia(`only screen and (min-width: ${breakpoint}px)`).matches
}

export const makeIdSafe = (inputString: string) => {
    if (!inputString) {
        return ''
    }

    // Replace invalid characters with underscores
    const sanitizedString = inputString.replace(/[^a-zA-Z0-9-_:.]/g, '_')

    // Ensure the ID starts with a letter
    if (!/[a-zA-Z]/.test(sanitizedString.charAt(0))) {
        return 'a' + sanitizedString.substring(1)
    }

    return sanitizedString.toLowerCase()
}

export const sanitize = (text: string): string => storybookSanitize(text.replaceAll('&shy;', ''))

export const pushEventToDataLayer = (eventName: string, eventOptions?: Record<string, string>) => {
    // @ts-ignore
    if (window?.dataLayer) {
        // @ts-ignore
        window.dataLayer.push({
            'event': eventName,
            ...eventOptions
        })
    }
}

type StartDateInfo = {
    frequency: "monthly" | "daily" | null;
    nextDate: string | null;
};
  
interface DatePickerRules {
    includeDates?: Date[];
    minDate?: Date;
    maxDate?: Date;
}
  
export const getDatePickerLimits = ({
    startDate,
    contractStart,
    contractEnd,
}: {
    startDate: StartDateInfo;
    contractStart: string;
    contractEnd: string;
}): DatePickerRules => {
    const nextDate = startDate?.nextDate ? new Date(startDate.nextDate) : null
    const start = new Date(contractStart)
    const end = new Date(contractEnd)
    let dateList = [];
    
    const diffInDays = moment().diff(moment(contractStart), 'days')
    
    if (diffInDays <= 14) {
        dateList.push(start)
    }

    if (startDate.frequency === 'monthly' && nextDate) {
        const anchor = moment(nextDate)
        const endMoment = moment(end)
        let i = 0
        let current = anchor.clone()

        while (current.isSameOrBefore(endMoment)) {
            dateList.push(current.toDate())
            i++
            current = anchor.clone().add(i, 'months')
        }

        return { includeDates: dateList }
    }
  
    if (startDate.frequency === 'daily' && nextDate) {
        let current = new Date(nextDate)
  
        while (current <= end) {
            dateList.push(new Date(current))
            current.setDate(current.getDate() + 1)
        }
  
        return { includeDates: dateList }
    }

    return {
        minDate: new Date(),
        maxDate: end
    };
}
  

export const trimWhitespaces = (value: string) => value.trimStart().replace(/\s\s+/g, ' ').trim()

export const errorToMessage = (e: unknown, errorMessage?: string) =>
    e instanceof Error ? e.message : errorMessage ?? 'Error occurred'

export const isGeneratedEmail = (email: string) =>
    email.includes('@inzmo.com') || email.includes('@as.check24.de')
  
